import useIsMobile from 'Common/useIsMobile';
import { Eye, EyeOff } from 'lucide-react';
import React, { ReactNode, useState } from 'react';

interface CommonInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  suffix?: string | ReactNode;
  error?: string;
  onlyBorderError?: boolean;
  allowPasswordToggle?: boolean;
  selectAllOnFocus?: boolean;
}

const Input: React.FC<CommonInputProps> = ({
  label,
  required,
  className,
  suffix = '',
  error,
  value,
  onlyBorderError = false,
  allowPasswordToggle = false,
  selectAllOnFocus = false,
  type,
  onFocus,
  onClick,
  ...rest
}) => {
  const isMobile = useIsMobile();
  const [showPassword, setShowPassword] = useState(false);
  const hasPasswordToggle = allowPasswordToggle && type === 'password';
  const inputType = hasPasswordToggle ? (showPassword ? 'text' : 'password') : type;

  const handleSelectAll = (event: React.FocusEvent<HTMLInputElement> | React.MouseEvent<HTMLInputElement>) => {
    if (!selectAllOnFocus) return;
    event.currentTarget.select();
  };

  return (
    <div className="w-full">
      {label && (
        <label
          className={`block ${isMobile ? 'text-16' : 'text-base'} font-semibold text-slate-800 dark:text-zink-100 ${label ? 'mb-1' : ''}`}
        >
          {required && <span className="text-red-500 mr-1">*</span>}
          {label}
        </label>
      )}
      <div className="relative">
        <input
          {...rest}
          type={inputType}
          value={value ?? ''}
          required={required}
          aria-invalid={Boolean(error)}
          onFocus={(event) => {
            handleSelectAll(event);
            onFocus?.(event);
          }}
          onClick={(event) => {
            handleSelectAll(event);
            onClick?.(event);
          }}
          className={`px-3 py-2 text-blue-default sm:text-slate-800 placeholder:text-slate-400 dark:text-zink-100 dark:placeholder:text-zink-400 ${hasPasswordToggle ? 'pr-11' : ''} ${error ? '!border-red-500 focus:border-red-500' : ''} ${className || ''}`}
        />
        {hasPasswordToggle && (
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 transition-colors hover:text-blue-default dark:text-zink-300"
            aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiển thị mật khẩu'}
          >
            {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        )}
        {suffix && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 whitespace-nowrap text-[12px] leading-[16px] font-medium text-blue-default sm:text-slate-500 dark:text-zink-300 pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
      {error && !onlyBorderError && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
};

export default Input;
